import { useEffect, useState } from "react";
import { useSite } from "@/context/SiteContext";
import { PageHeader } from "@/components/common/PageHeader";
import { KpiCard } from "@/components/common/KpiCard";
import { Loader } from "@/components/common/Loader";
import { eur } from "@/lib/format";
import { openPdf } from "@/lib/pdf";
import api from "@/lib/api";
import { CalendarCheck, Download, TrendingUp, TrendingDown, CheckCircle2, AlertTriangle, Sparkles, Loader2 } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const G = "#10B981";
const monthLabel = (ym) => {
  if (!ym) return "";
  const [y, m] = ym.split("-");
  return new Date(Number(y), Number(m) - 1, 1).toLocaleString("en-GB", { month: "short", year: "numeric" });
};

function Delta({ pct }) {
  if (pct === null || pct === undefined) return <span className="text-xs text-[#8a8d90]">—</span>;
  const up = pct >= 0;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold ${up ? "text-emerald-600" : "text-red-600"}`}>
      {up ? <TrendingUp size={12} /> : <TrendingDown size={12} />}{up ? "+" : ""}{pct}%
    </span>
  );
}

const Metric = ({ l, v }) => (
  <div className="rounded-lg bg-[#f7f8fa] px-3 py-2">
    <div className="text-[11px] uppercase tracking-wide text-[#6a6d70]">{l}</div>
    <div className="mt-0.5 text-lg font-semibold text-[#161d23]">{v}</div>
  </div>
);

const PnlRow = ({ l, v, b, bold, muted, sub, highlight, showVar = true }) => {
  const varv = (v ?? 0) - (b ?? 0);
  return (
    <tr className={`border-t border-[#f0f2f5] ${highlight ? "bg-[#eaf7f1]" : ""}`}>
      <td className={`py-1.5 ${bold ? "font-semibold" : ""} ${muted ? "text-[#6a6d70]" : ""}`}>{l}{sub && <span className="ml-2 text-[10px] text-[#8a8d90]">{sub}</span>}</td>
      <td className={`py-1.5 text-right font-mono ${bold ? "font-semibold" : ""} ${v < 0 ? "text-red-600" : ""}`}>{eur(v)}</td>
      <td className="py-1.5 text-right font-mono text-[#6a6d70]">{b === undefined ? "—" : eur(b)}</td>
      <td className={`py-1.5 text-right font-mono ${showVar ? (varv < 0 ? "text-red-600" : "text-emerald-600") : "text-[#c8cfda]"}`}>{showVar ? eur(varv) : "—"}</td>
    </tr>
  );
};

const AgeRow = ({ l, b, t }) => (
  <tr className="border-t border-[#f0f2f5] text-right">
    <td className="py-1.5 text-left font-medium">{l}</td>
    <td className="font-mono">{eur(b.current)}</td><td className="font-mono">{eur(b.d1_30)}</td>
    <td className="font-mono">{eur(b.d31_60)}</td><td className="font-mono">{eur(b.d61_90)}</td>
    <td className="font-mono">{eur(b.d90_plus)}</td><td className="font-mono font-semibold">{eur(t)}</td>
  </tr>
);

export default function MonthEndClose() {
  const { activeSite } = useSite();
  const [month, setMonth] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ai, setAi] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  const loadAi = async (mo) => {
    setAiLoading(true); setAi(null);
    try {
      const { data } = await api.get("/fi/month-end-pack/commentary", { params: { month: mo || undefined, site: activeSite } });
      setAi(data.commentary);
    } catch { setAi(null); } finally { setAiLoading(false); }
  };
  const load = async (mo) => {
    setLoading(true);
    try {
      const { data } = await api.get("/fi/month-end-pack", { params: { month: mo || undefined, site: activeSite } });
      setData(data); setMonth(data.period.month);
      loadAi(data.period.month);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(month); /* eslint-disable-next-line */ }, [activeSite]);

  if (loading && !data) return <Loader label="Compiling month-end pack…" />;
  if (!data) return null;
  const { pnl, kpis, ar_aging, ap_aging, working_capital, cost_centers, checklist, revenue_trend, top_customers, financials, period, fy_budget } = data;
  const dl = () => openPdf(`/fi/month-end-pack/pdf?month=${period.month}&site=${activeSite}`);

  return (
    <div data-testid="month-end-close">
      <PageHeader title="Month-End Close Pack" subtitle={`Period-close analysis for ${period.scope}. P&L, aging, working capital, cost-centre variance and close checklist.`} icon={CalendarCheck} accent={G}>
        <div className="flex items-center gap-2">
          <select data-testid="mec-month" value={month} onChange={(e) => { setMonth(e.target.value); load(e.target.value); }} className="rounded-lg border border-[#e5e9f0] bg-white px-3 py-2 text-sm">
            {data.available_months.slice().reverse().map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
          </select>
          <button data-testid="mec-pdf-btn" onClick={dl} className="inline-flex items-center gap-2 rounded-lg bg-[#10B981] px-4 py-2 text-sm font-semibold text-white hover:bg-[#059669]"><Download size={16} /> Download PDF pack</button>
        </div>
      </PageHeader>

      <section className="mb-6 rounded-xl border border-[#cdeee7] bg-gradient-to-br from-[#f0fdfa] to-white p-5" data-testid="mec-ai-commentary">
        <div className="mb-2 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-[#0f766e]"><Sparkles size={16} /> AI Close Commentary</div>
        {aiLoading ? <div className="flex items-center gap-2 text-sm text-[#6a6d70]"><Loader2 className="animate-spin" size={14} /> Generating narrative…</div>
          : ai ? <p className="text-sm leading-relaxed text-[#32363a]">{ai}</p>
            : <p className="text-sm text-[#8a8d90]">Commentary unavailable right now.</p>}
      </section>

      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <KpiCard label={`Revenue · ${period.label}`} value={eur(pnl.revenue)} accent={G} />
        <KpiCard label="Gross Profit" value={eur(pnl.gross)} accent="#0a6ed1" />
        <KpiCard label="Net Profit" value={eur(pnl.net)} accent={pnl.net >= 0 ? "#107e3e" : "#bb0000"} />
        <KpiCard label="Working Capital" value={eur(working_capital.working_capital)} accent="#06B6D4" />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <section className="rounded-xl border border-[#e5e9f0] bg-white p-5" data-testid="mec-pnl">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-[#354a5f]">Profit &amp; Loss</h3>
            <div className="flex gap-3"><span className="text-xs text-[#6a6d70]">MoM <Delta pct={pnl.mom_pct} /></span><span className="text-xs text-[#6a6d70]">YoY <Delta pct={pnl.yoy_pct} /></span></div>
          </div>
          <div className="overflow-x-auto">
          <table className="w-full min-w-[480px] text-sm">
            <thead className="text-[11px] uppercase text-[#6a6d70]"><tr><th className="py-1 text-left">Line</th><th className="text-right">Actual</th><th className="text-right">Budget</th><th className="text-right">Variance</th></tr></thead>
            <tbody>
              <PnlRow l="Revenue" v={pnl.revenue} b={pnl.budget.revenue} />
              <PnlRow l="Cost of Goods Sold (est.)" v={-pnl.cogs} b={-pnl.budget.cogs} muted showVar={false} />
              <PnlRow l="Gross Profit" v={pnl.gross} b={pnl.budget.gross} bold sub={`${pnl.gross_margin_pct}% margin`} />
              <PnlRow l="Manufacturing Overhead (est.)" v={-pnl.overhead} b={-pnl.budget.overhead} muted showVar={false} />
              <PnlRow l="Freight & Distribution (est.)" v={-pnl.freight} b={-pnl.budget.freight} muted showVar={false} />
              <PnlRow l="Net Profit" v={pnl.net} b={pnl.budget.net} bold sub={`${pnl.net_margin_pct}% margin`} highlight />
            </tbody>
          </table>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-[#f0f2f5] pt-3 text-xs text-[#6a6d70]">
            <span>FY{fy_budget.year} budget <b className="text-[#161d23]">{eur(fy_budget.budget)}</b></span>
            <span>YTD actual <b className="text-[#161d23]">{eur(fy_budget.ytd_actual)}</b> vs budget {eur(fy_budget.ytd_budget)}</span>
            <span>YTD variance <b className={fy_budget.ytd_variance < 0 ? "text-red-600" : "text-emerald-600"}>{eur(fy_budget.ytd_variance)}</b>{fy_budget.ytd_variance_pct != null && ` (${fy_budget.ytd_variance_pct > 0 ? "+" : ""}${fy_budget.ytd_variance_pct}%)`}</span>
          </div>
          <p className="mt-2 text-[10px] text-[#8a8d90]">Budget basis: {fy_budget.basis}. COGS &amp; overhead estimated from GL cost ratios applied to period revenue.</p>
        </section>

        <section className="rounded-xl border border-[#e5e9f0] bg-white p-5" data-testid="mec-kpis">
          <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-[#354a5f]">Working Capital &amp; Ratios</h3>
          <div className="grid grid-cols-2 gap-3">
            <Metric l="Cash Position" v={eur(working_capital.cash)} />
            <Metric l="Current Ratio" v={kpis.current_ratio ?? "—"} />
            <Metric l="Open AR" v={eur(working_capital.ar_open)} />
            <Metric l="Open AP" v={eur(working_capital.ap_open)} />
            <Metric l="DSO (days)" v={kpis.dso ?? "—"} />
            <Metric l="DPO (days)" v={kpis.dpo ?? "—"} />
            <Metric l="Gross Margin" v={`${kpis.gross_margin_pct}%`} />
            <Metric l="Net Margin" v={`${kpis.net_margin_pct}%`} />
          </div>
        </section>
      </div>

      <section className="mt-6 rounded-xl border border-[#e5e9f0] bg-white p-5" data-testid="mec-aging">
        <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-[#354a5f]">Receivables &amp; Payables Aging <span className="font-normal text-[#8a8d90]">(as at {period.period_end})</span></h3>
        <div className="overflow-x-auto">
        <table className="w-full min-w-[560px] text-sm">
          <thead className="text-[11px] uppercase text-[#6a6d70]"><tr className="text-right"><th className="py-1 text-left">Ledger</th><th>Current</th><th>1-30</th><th>31-60</th><th>61-90</th><th>90+</th><th>Total</th></tr></thead>
          <tbody>
            <AgeRow l="Receivables (AR)" b={ar_aging.buckets} t={ar_aging.total} />
            <AgeRow l="Payables (AP)" b={ap_aging.buckets} t={ap_aging.total} />
          </tbody>
        </table>
        </div>
        {ar_aging.overdue > 0 && <p className="mt-2 text-xs text-red-600">Overdue receivables: {eur(ar_aging.overdue)}</p>}
      </section>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <section className="rounded-xl border border-[#e5e9f0] bg-white p-5" data-testid="mec-trend">
          <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-[#354a5f]">Revenue Trend</h3>
          <div style={{ width: "100%", height: 240, minHeight: 240 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={revenue_trend} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef1f5" />
                <XAxis dataKey="month" tick={{ fontSize: 10 }} tickFormatter={monthLabel} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `£${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(v) => eur(v)} labelFormatter={monthLabel} />
                <Bar dataKey="revenue" fill={G} radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </section>

        <section className="rounded-xl border border-[#e5e9f0] bg-white p-5" data-testid="mec-costcenters">
          <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-[#354a5f]">Cost Centre — Plan vs Actual</h3>
          <div className="overflow-x-auto">
          <table className="w-full min-w-[420px] text-sm">
            <thead className="text-[11px] uppercase text-[#6a6d70]"><tr><th className="py-1 text-left">Centre</th><th className="text-right">Plan</th><th className="text-right">Actual</th><th className="text-right">Variance</th></tr></thead>
            <tbody>
              {cost_centers.rows.map((r) => (
                <tr key={r.cc_code} className="border-t border-[#f0f2f5]">
                  <td className="py-1.5">{r.name}<div className="font-mono text-[10px] text-[#6a6d70]">{r.cc_code}</div></td>
                  <td className="text-right font-mono">{eur(r.plan)}</td>
                  <td className="text-right font-mono">{eur(r.actual)}</td>
                  <td className={`text-right font-mono ${r.variance < 0 ? "text-red-600" : "text-emerald-600"}`}>{eur(r.variance)}</td>
                </tr>
              ))}
              <tr className="border-t-2 border-[#32363a] font-semibold">
                <td className="py-1.5">Total</td>
                <td className="text-right font-mono">{eur(cost_centers.totals.plan)}</td>
                <td className="text-right font-mono">{eur(cost_centers.totals.actual)}</td>
                <td className={`text-right font-mono ${cost_centers.totals.variance < 0 ? "text-red-600" : "text-emerald-600"}`}>{eur(cost_centers.totals.variance)}</td>
              </tr>
            </tbody>
          </table>
          </div>
        </section>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <section className="rounded-xl border border-[#e5e9f0] bg-white p-5" data-testid="mec-checklist">
          <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-[#354a5f]">Month-End Close Checklist</h3>
          <div className="space-y-2">
            {checklist.map((c) => (
              <div key={c.item} data-testid={`mec-chk-${c.item.replace(/\s+/g, "-").toLowerCase()}`} className="flex items-start gap-3 rounded-lg border border-[#f0f2f5] px-3 py-2">
                {c.status === "ok" ? <CheckCircle2 size={18} className="mt-0.5 shrink-0 text-emerald-600" /> : <AlertTriangle size={18} className="mt-0.5 shrink-0 text-amber-500" />}
                <div className="flex-1">
                  <div className="flex justify-between text-sm font-medium"><span>{c.item}</span><span className="font-mono">{c.value}</span></div>
                  <div className="text-xs text-[#8a8d90]">{c.detail}</div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="rounded-xl border border-[#e5e9f0] bg-white p-5" data-testid="mec-top-customers">
          <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-[#354a5f]">Top Customers — {period.label}</h3>
          {top_customers.length === 0 ? <p className="text-sm text-[#8a8d90]">No sales in this period.</p> : (
            <table className="w-full text-sm"><tbody>
              {top_customers.map((c) => (
                <tr key={c.customer_code} className="border-t border-[#f0f2f5] first:border-0">
                  <td className="py-1.5">{c.customer_name}<div className="font-mono text-[10px] text-[#6a6d70]">{c.customer_code}</div></td>
                  <td className="text-right font-mono">{eur(c.revenue)}</td>
                </tr>
              ))}
            </tbody></table>
          )}
        </section>
      </div>

      <section className="mt-6 rounded-xl border border-[#e5e9f0] bg-white p-5" data-testid="mec-balancesheet">
        <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-[#354a5f]">Balance Sheet <span className="font-normal text-[#8a8d90]">(company, period end)</span></h3>
        <div className="grid grid-cols-3 gap-4">
          <Metric l="Total Assets" v={eur(financials.balance_sheet.asset_total)} />
          <Metric l="Total Liabilities" v={eur(financials.balance_sheet.liability_total)} />
          <Metric l="Total Equity" v={eur(financials.balance_sheet.equity_total)} />
        </div>
      </section>
    </div>
  );
}
